// The call's settings, behind a gear.
//
// The panel floats over the console and every row it spends is a row of map or roster the operator
// cannot see, so the microphone picker — which most calls never need — collapses to one icon in the
// controls row and opens UPWARDS over the video rather than pushing the panel taller.
//
// The gear turns amber when the microphone is sending silence: the warning above says what is wrong,
// and this says where the fix is.

import { Settings } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MAX_GAIN, MIN_GAIN } from './mic-chain'
import { MicLevelMeter } from './MicLevelMeter'
import { MicPicker } from './MicPicker'

export interface CallSettingsMenuProps {
  onSelectMic: (deviceId: string) => Promise<void>
  /** The call is transmitting silence — point at the fix. */
  urgent?: boolean
  /** Live peak of the transmitted signal, for the meter. */
  peak: number | null
  muted?: boolean
  gain: number
  onGainChange: (value: number) => void
}

export function CallSettingsMenu({
  onSelectMic,
  urgent = false,
  peak,
  muted = false,
  gain,
  onGainChange,
}: CallSettingsMenuProps) {
  const { t } = useTranslation('console')
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return

    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }

    // Escape closes the MENU and must not reach the panel behind it — on an emergency call the key
    // that ends things is Hang up, pressed deliberately.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
    }

    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey, true)

    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={t('alerts.sos.call.settings', 'Call settings')}
        title={t('alerts.sos.call.settings', 'Call settings')}
        data-testid="sos-call-settings"
        className={`flex items-center rounded-md px-2 py-1.5 hover:bg-gray-700 ${
          urgent ? 'bg-amber-500/20 text-amber-300' : 'bg-gray-800 text-gray-100'
        }`}
      >
        <Settings className="h-4 w-4" aria-hidden />
      </button>

      {open && (
        // Anchored bottom-left and opening UPWARDS: the controls row is at the foot of the panel, so
        // a downward menu would fall off the bottom of a panel snapped to the bottom edge.
        <div
          data-testid="sos-call-settings-menu"
          className="absolute bottom-full left-0 z-20 mb-2 w-56 rounded-lg border border-gray-700 bg-gray-900 p-2 shadow-2xl"
        >
          <div className="flex flex-col gap-3">
            <MicPicker onSelect={onSelectMic} />

            {/* The meter sits between the device and the gain because it answers both questions:
                whether this input hears anything at all, and whether the boost is enough. */}
            <MicLevelMeter peak={peak} muted={muted} />

            <label className="flex flex-col gap-1 text-xs text-gray-300">
              <span>
                {t('alerts.sos.call.micGain', 'Microphone boost')} ({gain.toFixed(1)}×)
              </span>
              <input
                type="range"
                min={MIN_GAIN}
                max={MAX_GAIN}
                step={0.1}
                value={gain}
                onChange={e => onGainChange(Number(e.target.value))}
                data-testid="sos-call-mic-gain"
                className="w-full accent-blue-500"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  )
}
